// lib/outbox.ts — the crew's offline outbox (FW-007).
//
// A crew in a back yard with no signal still clocks in and out, takes a
// break, taps "On my way", starts and finishes the job, and leaves notes and
// checklist ticks. Each of those is an OPERATION:
//
//   { id, kind, jobId, payload, created_at, attempts, last_error }
//
//   id          a key made on the phone — the service applies an op once per
//               key, however many times it's sent (server/src/crewops.js)
//   created_at  the TRUE time of the action: a clock-in at 7:02 with no signal
//               is recorded as 7:02, not when it synced
//
// The op is applied to the phone's copy of the job at once (applyFieldOp, the
// same rules the service runs — server/src/fieldops.js; unit-outbox.mjs keeps
// the two in step), and kept here until the service acknowledges it:
//
//   • durable: IndexedDB, or localStorage where IndexedDB isn't available; it
//     survives a reload, the app being swiped away, the phone restarting
//   • in order per job: a job's ops go one at a time, oldest first, and a job
//     whose op is waiting (backoff) or refused waits behind it — other jobs
//     carry on
//   • flushed when the signal comes back (`online`), when the app comes to the
//     front, on a timer with backoff (2 s, 4 s … 5 min), and from the service
//     worker's Background Sync where the browser has it (public/outbox-sw.js)
//   • a permanent refusal (a 4xx that isn't "try again") is NOT retried
//     forever: the op stops, says why, and the crew chooses discard or retry
//
// No imports: scripts/unit-outbox.mjs transpiles this file on its own and runs
// it in node against a fake IndexedDB, a fake localStorage and a fake fetch.

/* ============================================================
   Types
   ============================================================ */
export type FieldKind = "clock.in" | "break.start" | "break.end" | "clock.out" | "job.enroute" | "job.start" | "job.complete" | "job.note" | "job.check" | "job.liftlog";
export type OutboxEntry = {
  id: string; kind: FieldKind; jobId: string; payload: any;
  created_at: number;            // when the crew did it (ms)
  attempts: number; last_error: string | null;
  status: "queued" | "failed";   // failed = refused for good, waiting on the crew's choice
  seq: number;                   // order of entry on this device
  nextAt?: number;               // backoff: not before
  ws?: string | null;
  queuedOffline?: boolean;       // the phone knew it had no signal when this was made
};
export type Recent = {
  id: string; kind: FieldKind; jobId: string; created_at: number; payload: any;
  ackAt: number; result: "applied" | "held" | "discarded";
  message?: string | null; stamp?: any; dispatch?: any;
};
export type SendResult = { status: number; body?: any };
export type Outcome = "ok" | "held" | "retry" | "auth" | "permanent" | "offline";

export const FIELD_KINDS: FieldKind[] = ["clock.in", "break.start", "break.end", "clock.out", "job.enroute", "job.start", "job.complete", "job.note", "job.check", "job.liftlog"];
/* the concrete crew's lift log (lib/concrete.actuals.ts; server/src/fieldops.js mirrors these) */
export const LIFT_LOG_MAX = 80;
const LIFT_LIMITS = { inches: 36, foamLb: 5000, holes: 999, sqft: 100000, section: 80, note: 500 };
const liftEntry = (e: any, at: number, stamp: any) => {
  if (!e || typeof e !== "object" || !e.id || !/^[A-Za-z0-9_.:-]{3,80}$/.test(String(e.id))) return null;
  const n = (v: any, max: number) => { const x = Number(v); return Number.isFinite(x) ? Math.max(0, Math.min(max, x)) : 0; };
  const out: any = { id: String(e.id), section: String(e.section || "").trim().slice(0, LIFT_LIMITS.section) || "Section",
    beforeIn: Math.round(n(e.beforeIn, LIFT_LIMITS.inches) * 100) / 100, afterIn: Math.round(n(e.afterIn, LIFT_LIMITS.inches) * 100) / 100,
    foamLb: Math.round(n(e.foamLb, LIFT_LIMITS.foamLb) * 10) / 10, holes: Math.round(n(e.holes, LIFT_LIMITS.holes)), at: Number(e.at) > 0 ? Number(e.at) : at };
  if (Number(e.sqft) > 0) out.sqft = Math.round(n(e.sqft, LIFT_LIMITS.sqft));
  const note = String(e.note || "").trim().slice(0, LIFT_LIMITS.note); if (note) out.note = note;
  if (e.crewId) out.crewId = String(e.crewId).slice(0, 80);
  if (stamp) out.offline = stamp;
  return out;
};
/* the warranty terms dated from completion (lib/concrete.actuals.ts datedWarranty, kept import-free here) */
const datedWarranty = (terms: any, completedAt: number) => (Array.isArray(terms) ? terms : []).filter((t) => t && t.jobType).map((t) => {
  const y = Number(t.years) > 0 ? Number(t.years) : null;
  return { jobType: t.jobType, years: y, text: String(t.text || ""), until: y && Number(completedAt) > 0 ? new Date(Number(completedAt) + y * 365.25 * 86400000).toISOString().slice(0, 10) : null };
});
export const FIELD_OPS_KEEP = 300;
export const NOTE_MAX = 2000;
export const RECENT_KEEP_MS = 30 * 60 * 1000;   // acked ops stay on the phone's view this long (until a fresh copy of the job has them)
export const RECENT_MAX = 150;
export const BACKOFF_BASE_MS = 2000;
export const BACKOFF_MAX_MS = 5 * 60 * 1000;
export const LS_KEY = "fw_outbox_v1";
export const IDB_NAME = "fw-outbox";
export const SYNC_TAG = "fw-outbox";

/* The on-site checklist in Crew Mode (the crew sheet's "before onsite" list
   plus the two things the office asks about after). Ids are what's stored. */
export const CHECK_LIST: [string, string][] = [
  ["811", "811 flags checked"],
  ["lines", "Property lines confirmed"],
  ["string", "String line snapped"],
  ["materials", "Materials on site"],
  ["gates", "Gates swing and latch"],
  ["cleanup", "Site cleaned up"],
];
/* …and the concrete job's (lib/concrete.steps.ts concreteChecklistIds picks
   the ids a job's types need; server/src/fieldops.js mirrors them). The fence
   list above is untouched: a fence job's checklist is byte-identical. */
export const CONCRETE_CHECK_LIST: [string, string][] = [
  ["cx_access", "Rig in place, hoses reach"],
  ["cx_before", "Before photos taken"],
  ["cx_heat", "In-floor heat & utilities checked"],
  ["cx_line", "Lift line marked"],
  ["cx_patched", "Holes patched flush"],
  ["cx_moisture", "Moisture test done"],
  ["cx_cure", "Cure times told to the customer"],
  ["cx_after", "After photos taken"],
  ["cx_cleanup", "Site cleaned up"],
];
/* …and what the crew noticed for the OTHER service (plan §5.6 cross-sell): a
   fence crew ticks the sunken walk, a concrete crew the leaning fence; the
   office's cross-sell rules read them off the finished job (app.tsx
   job.completed → job.noted; server/src/fieldops.js mirrors the ids). Shown on
   a fence job only when the workspace runs concrete (crew.tsx). */
export const CROSS_SELL_CHECKS: [string, string][] = [
  ["xs_concrete", "Noticed sunken or cracked concrete (walk, drive, steps)"],
  ["xs_fence", "Noticed an old or leaning fence"],
];
export const checkLabel = (id: any): string | null => { const hit = CHECK_LIST.find(([k]) => k === id) || CONCRETE_CHECK_LIST.find(([k]) => k === id) || CROSS_SELL_CHECKS.find(([k]) => k === id); return hit ? hit[1] : null; };

/* ============================================================
   What an op does to a job — a port of server/src/fieldops.js
   ============================================================ */
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
const DONE = /^(done|completed)$/i;
const CLOSED = /^(done|completed|cancel+ed)$/i;
function clean(o: any) { const out: any = {}; for (const k of Object.keys(o)) if (o[k] !== undefined && o[k] !== null) out[k] = o[k]; return out; }

export function storedOverride(ov: any) {
  if (!ov || typeof ov !== "object") return undefined;
  const reason = String(ov.reason || "").trim().slice(0, 200);
  if (!reason) return undefined;
  return clean({ reason, at: Number(ov.at) || undefined, withCode: ov.withCode ? true : undefined });
}
export function storedGps(g: any) {
  if (!g || typeof g !== "object") return undefined;
  const out: any = {};
  if (isFinite(+g.lat) && isFinite(+g.lng) && g.lat !== null && g.lng !== null) { out.lat = Math.round(+g.lat * 1e6) / 1e6; out.lng = Math.round(+g.lng * 1e6) / 1e6; }
  if (isFinite(+g.accuracyM) && g.accuracyM !== null) out.accuracyM = Math.round(+g.accuracyM);
  if (g.distMi != null && isFinite(+g.distMi)) out.distMi = Math.round(+g.distMi * 100) / 100;
  if (typeof g.ok === "boolean") out.ok = g.ok;
  if (g.noSite) out.noSite = true;
  if (g.error) out.error = String(g.error).slice(0, 120);
  const ov = storedOverride(g.override);
  if (ov) out.override = ov;
  return Object.keys(out).length ? out : undefined;
}

export type Op = { id: string; kind: FieldKind | string; at: number; payload?: any };
export const opOf = (e: { id: string; kind: any; created_at: number; payload?: any }): Op => ({ id: e.id, kind: e.kind, at: e.created_at, payload: e.payload || {} });
const sidOf = (op: Op) => String((op.payload && op.payload.sid) || op.id);

export function applyFieldOp(wo: any, op: Op, ctx: { stamp?: any } = {}) {
  if (!wo || typeof wo !== "object" || !op || !op.id || !FIELD_KINDS.includes(op.kind as FieldKind)) return wo;
  const seen = arr(wo.fieldOps);
  if (seen.includes(op.id)) return wo;
  const p = op.payload || {};
  const at = Number(op.at) || 0;
  const stamp = ctx.stamp || undefined;
  const w: any = { ...wo };
  const sid = sidOf(op);
  const open: any = { ...(wo.clockOpen && typeof wo.clockOpen === "object" ? wo.clockOpen : {}) };
  switch (op.kind) {
    case "clock.in": {
      if (arr(w.timeLogs).some((e) => e && e.sessionId === sid)) break;
      if (!open[sid]) {
        open[sid] = clean({ at, gps: storedGps(p.gps), override: storedOverride(p.override), crewId: p.crewId || undefined, offline: stamp });
        w.clockOpen = open;
      }
      break;
    }
    case "break.start": {
      const s = open[sid];
      if (s && !s.breakAt) { open[sid] = { ...s, breakAt: at }; w.clockOpen = open; }
      break;
    }
    case "break.end": {
      const s = open[sid];
      if (s && s.breakAt) {
        const { breakAt, ...rest } = s;
        open[sid] = { ...rest, breakMs: (Number(s.breakMs) || 0) + Math.max(0, at - Number(breakAt)) };
        w.clockOpen = open;
      }
      break;
    }
    case "clock.out": {
      const e0 = p.entry;
      if (!e0 || !e0.id) break;
      const logs = arr(w.timeLogs);
      if (!logs.some((e) => e && e.id === e0.id)) {
        const s = open[sid];
        const entry = clean({ ...e0, gps: storedGps(e0.gps), gpsOut: storedGps(e0.gpsOut), sessionId: sid, offline: stamp, offlineIn: s && s.offline ? s.offline : undefined });
        w.timeLogs = [...logs, entry];
      }
      if (open[sid]) { delete open[sid]; w.clockOpen = open; }
      break;
    }
    case "job.enroute": {
      w.enrouteAt = at;
      if (stamp) w.enrouteOffline = stamp; else delete w.enrouteOffline;
      break;
    }
    case "job.start": {
      const st = String(w.status || "");
      if (!CLOSED.test(st) && st !== "in_progress") {
        w.history = [...arr(w.history), clean({ kind: "status", at, from: st || null, to: "in_progress", by: "crew", offline: stamp })].slice(-200);
        w.status = "in_progress";
      }
      if (!w.startedAt) w.startedAt = at;
      break;
    }
    case "job.complete": {
      const st = String(w.status || "");
      if (!DONE.test(st)) {
        w.history = [...arr(w.history), clean({ kind: "status", at, from: st || null, to: "completed", by: "crew", offline: stamp })].slice(-200);
        w.status = "completed";
      }
      if (!w.completedAt) w.completedAt = at;
      // the warranty terms the job carries (wo.cx.warranty, stamped from the
      // rate cards when the job was made) are dated from the day it finished
      if (!Array.isArray(w.warranty) && w.cx && Array.isArray(w.cx.warranty) && w.cx.warranty.length) w.warranty = datedWarranty(w.cx.warranty, w.completedAt);
      break;
    }
    case "job.note": {
      const n = p.note || {};
      const text = String(n.text || "").trim().slice(0, NOTE_MAX);
      if (!n.id || !text) break;
      const notes = arr(w.fieldNotes);
      if (!notes.some((x) => x && x.id === n.id)) w.fieldNotes = [...notes, clean({ id: String(n.id), text, at: Number(n.at) || at, crewId: n.crewId || undefined, by: n.by ? String(n.by).slice(0, 80) : undefined, offline: stamp })];
      break;
    }
    case "job.liftlog": {
      // a lift-log entry (lib/concrete.actuals.ts): upsert by id, the newer
      // `at` wins; `removeId` takes one off. Capped so a runaway phone can't
      // grow the row without bound.
      const list = arr(w.liftLog).filter(Boolean);
      if (p.removeId) { w.liftLog = list.filter((x) => x.id !== String(p.removeId)); break; }
      const e = liftEntry(p.entry, at, stamp);
      if (!e) break;
      const i = list.findIndex((x) => x && x.id === e.id);
      if (i >= 0) { if (!(Number(list[i].at) > e.at)) { const next = list.slice(); next[i] = e; w.liftLog = next; } }
      else w.liftLog = [...list, e].slice(-LIFT_LOG_MAX);
      break;
    }

    case "job.check": {
      const item = String(p.item || "").slice(0, 40);
      if (!item) break;
      const list = { ...(w.fieldChecklist && typeof w.fieldChecklist === "object" ? w.fieldChecklist : {}) };
      const cur = list[item];
      if (!(cur && Number(cur.at) > at)) { list[item] = clean({ done: !!p.done, at, offline: stamp }); w.fieldChecklist = list; }
      break;
    }
    default: return wo;
  }
  w.fieldOps = [...seen, op.id].slice(-FIELD_OPS_KEEP);
  return w;
}

export function stampFieldOp(wo: any, op: Op, stamp: any) {
  if (!wo || !op || !stamp) return wo;
  const p = op.payload || {};
  const sid = sidOf(op);
  switch (op.kind) {
    case "clock.in": {
      const s = wo.clockOpen && wo.clockOpen[sid];
      if (s && !s.offline) return { ...wo, clockOpen: { ...wo.clockOpen, [sid]: { ...s, offline: stamp } } };
      const logs = arr(wo.timeLogs);
      const i = logs.findIndex((e) => e && e.sessionId === sid && !e.offlineIn);
      if (i >= 0) { const next = logs.slice(); next[i] = { ...logs[i], offlineIn: stamp }; return { ...wo, timeLogs: next }; }
      return wo;
    }
    case "clock.out": {
      const id = p.entry && p.entry.id;
      const logs = arr(wo.timeLogs);
      const i = logs.findIndex((e) => e && e.id === id && !e.offline);
      if (i < 0) return wo;
      const next = logs.slice(); next[i] = { ...logs[i], offline: stamp };
      return { ...wo, timeLogs: next };
    }
    case "job.enroute":
      return Number(wo.enrouteAt) === Number(op.at) && !wo.enrouteOffline ? { ...wo, enrouteOffline: stamp } : wo;
    case "job.start": case "job.complete": {
      const to = op.kind === "job.start" ? "in_progress" : "completed";
      const hist = arr(wo.history);
      const i = hist.findIndex((h) => h && h.kind === "status" && h.to === to && Number(h.at) === Number(op.at) && !h.offline);
      if (i < 0) return wo;
      const next = hist.slice(); next[i] = { ...hist[i], offline: stamp };
      return { ...wo, history: next };
    }
    case "job.note": {
      const id = p.note && p.note.id;
      const notes = arr(wo.fieldNotes);
      const i = notes.findIndex((n) => n && n.id === id && !n.offline);
      if (i < 0) return wo;
      const next = notes.slice(); next[i] = { ...notes[i], offline: stamp };
      return { ...wo, fieldNotes: next };
    }
    case "job.check": {
      const item = String(p.item || "");
      const cur = wo.fieldChecklist && wo.fieldChecklist[item];
      if (!cur || Number(cur.at) !== Number(op.at) || cur.offline) return wo;
      return { ...wo, fieldChecklist: { ...wo.fieldChecklist, [item]: { ...cur, offline: stamp } } };
    }
    case "job.liftlog": {
      const id = p.entry && p.entry.id;
      const list = arr(wo.liftLog);
      const i = list.findIndex((e) => e && e.id === id && !e.offline);
      if (i < 0) return wo;
      const next = list.slice(); next[i] = { ...list[i], offline: stamp };
      return { ...wo, liftLog: next };
    }
    default: return wo;
  }
}

export function revertFieldOp(wo: any, op: Op) {
  if (!wo || !op || !op.id) return wo;
  const seen = arr(wo.fieldOps);
  if (!seen.includes(op.id)) return wo;
  const p = op.payload || {};
  const sid = sidOf(op);
  const at = Number(op.at) || 0;
  const w: any = { ...wo, fieldOps: seen.filter((x) => x !== op.id) };
  switch (op.kind) {
    case "clock.in":
      if (w.clockOpen && w.clockOpen[sid]) { const o = { ...w.clockOpen }; delete o[sid]; w.clockOpen = o; }
      break;
    case "break.start": {
      const s = w.clockOpen && w.clockOpen[sid];
      if (s && Number(s.breakAt) === at) { const { breakAt, ...rest } = s; w.clockOpen = { ...w.clockOpen, [sid]: rest }; }
      break;
    }
    case "clock.out": {
      const id = p.entry && p.entry.id;
      w.timeLogs = arr(w.timeLogs).filter((e) => !(e && e.id === id));
      break;
    }
    case "job.enroute":
      if (Number(w.enrouteAt) === at) { delete w.enrouteAt; delete w.enrouteOffline; }
      break;
    case "job.start": case "job.complete": {
      const to = op.kind === "job.start" ? "in_progress" : "completed";
      const hist = arr(w.history);
      const hit = hist.map((h, k) => [h, k] as [any, number]).reverse().find(([h]) => h && h.kind === "status" && h.to === to && Number(h.at) === at && h.by === "crew");
      if (hit) { w.history = hist.filter((_, k) => k !== hit[1]); if (w.status === to) w.status = hit[0].from || w.status; }
      if (op.kind === "job.start" && Number(w.startedAt) === at) delete w.startedAt;
      if (op.kind === "job.complete" && Number(w.completedAt) === at) { delete w.completedAt; if (w.cx && Array.isArray(w.cx.warranty)) delete w.warranty; }
      break;
    }
    case "job.note": {
      const id = p.note && p.note.id;
      w.fieldNotes = arr(w.fieldNotes).filter((n) => !(n && n.id === id));
      break;
    }
    case "job.check": {
      const item = String(p.item || "");
      if (w.fieldChecklist && w.fieldChecklist[item] && Number(w.fieldChecklist[item].at) === at) { const l = { ...w.fieldChecklist }; delete l[item]; w.fieldChecklist = l; }
      break;
    }
    case "job.liftlog": {
      // an entry this op added comes off; a removal can't be undone from here (best effort, like the rest)
      const id = p.entry && p.entry.id;
      if (id) w.liftLog = arr(w.liftLog).filter((e) => !(e && e.id === id));
      break;
    }
    default: break;
  }
  return w;
}

/* One job in a state, changed by fn — the same state back when fn changed nothing. */
export function mapJob(state: any, jobId: string, fn: (w: any) => any) {
  const list = arr(state && state.workOrders);
  const i = list.findIndex((w) => w && w.id === jobId);
  if (i < 0) return state;
  const next = fn(list[i]);
  if (next === list[i]) return state;
  const out = list.slice(); out[i] = next;
  return { ...state, workOrders: out };
}

/* The phone's view: every op still on its way (and the ones acked lately,
   until a fresh copy of the job carries them) applied over whatever copy of
   the jobs the phone holds — so a reload, a realtime refresh or the boot read
   replacing the jobs never makes a crew's clock-in or note disappear from the
   screen while it's waiting for signal. Same object back when nothing moved. */
export function overlayOps(state: any, ops: Op[] & any[]) {
  let s = state;
  for (const e of ops || []) {
    if (!e || !e.jobId) continue;
    const op = "created_at" in e ? opOf(e) : e;
    s = mapJob(s, e.jobId, (w) => applyFieldOp(w, op));
  }
  return s;
}

/* Photos taken on this phone that haven't reached storage (pending: true, from
   lib/media.ts) are unioned back into a job a fresh copy dropped them from —
   only ones taken after the last save that fully landed, so a photo deleted on
   another device isn't brought back. */
export function rescuePendingPhotos(state: any, pending: { jobId: string; photo: any }[], sinceTs: number) {
  let s = state;
  for (const { jobId, photo } of pending || []) {
    if (!photo || !photo.url || !(Number(photo.at) > sinceTs)) continue;
    s = mapJob(s, jobId, (w) => (arr(w.photos).some((p) => p && (p.url === photo.url || (p.at === photo.at && p.name === photo.name))) ? w : { ...w, photos: [...arr(w.photos), photo] }));
  }
  return s;
}
/* the pending photos in a state, per job (what rescuePendingPhotos puts back) */
export function pendingPhotosOf(state: any) {
  const out: { jobId: string; photo: any }[] = [];
  for (const w of arr(state && state.workOrders)) for (const p of arr(w && w.photos)) if (p && p.pending) out.push({ jobId: w.id, photo: p });
  return out;
}

/* ============================================================
   The service's answer → what to do
   ============================================================ */
/* 200 applied / duplicate → done; 202 held → done (the office decides); a 4xx
   that isn't "try again" → stop and ask the crew; 401 → wait for sign-in;
   5xx, 408, 409 in-flight, 429 → back off and retry; no answer → offline. */
export function classify(res: SendResult | null | undefined): Outcome {
  if (!res || !res.status) return "offline";
  const s = res.status, b = res.body || {};
  if (s === 202 || b.status === "held") return s >= 200 && s < 300 ? "held" : "retry";
  if (s >= 200 && s < 300) return "ok";
  if (s === 401) return "auth";
  if (s >= 400 && s < 500 && ![408, 409, 425, 429].includes(s) && !b.retry) return "permanent";
  return "retry";
}

/* 2 s, 4 s, 8 s … capped at 5 minutes, ±20% so a crew of phones coming back
   into signal together don't all knock at once. */
export function backoffMs(attempts: number, rand = 0.5) {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * Math.pow(2, Math.max(0, attempts - 1)));
  return Math.round(base * (0.8 + 0.4 * Math.min(1, Math.max(0, rand))));
}

/* ============================================================
   Storage: IndexedDB, else localStorage, else memory
   ============================================================ */
/* acks: what the service worker's Background Sync got back for ops it sent
   while no page was open — the page takes them in on its next pass */
export type SwAck = { id: string; outcome: "ok" | "held"; body: any; at: number };
export type Snapshot = { entries: OutboxEntry[]; recent: Recent[]; seq: number; acks?: SwAck[] };
export interface OutboxStore {
  kind: "idb" | "local" | "memory";
  load(): Promise<Snapshot>;
  put(e: OutboxEntry): Promise<void>;
  del(id: string): Promise<void>;
  setRecent(list: Recent[]): Promise<void>;
  setMeta?(k: string, v: any): Promise<void>;
  clearAcks?(): Promise<void>;
}

export function memoryStore(): OutboxStore {
  const entries = new Map<string, OutboxEntry>();
  let recent: Recent[] = [];
  return {
    kind: "memory",
    async load() { const list = [...entries.values()].map((e) => ({ ...e })); return { entries: list, recent: recent.slice(), seq: list.reduce((m, e) => Math.max(m, e.seq || 0), 0) }; },
    async put(e) { entries.set(e.id, { ...e }); },
    async del(id) { entries.delete(id); },
    async setRecent(list) { recent = list.slice(); },
  };
}

export function localStore(ls: any, key = LS_KEY): OutboxStore {
  const read = (): { entries: OutboxEntry[]; recent: Recent[] } => {
    try { const v = JSON.parse(ls.getItem(key) || "null"); if (v && Array.isArray(v.entries)) return { entries: v.entries, recent: Array.isArray(v.recent) ? v.recent : [] }; } catch (e) { /* a bad copy is an empty one */ }
    return { entries: [], recent: [] };
  };
  const write = (v: any) => { ls.setItem(key, JSON.stringify(v)); };   // throws on a full device — the caller says so
  return {
    kind: "local",
    async load() { const v = read(); return { entries: v.entries, recent: v.recent, seq: v.entries.reduce((m, e) => Math.max(m, e.seq || 0), 0) }; },
    async put(e) { const v = read(); const i = v.entries.findIndex((x) => x.id === e.id); if (i >= 0) v.entries[i] = e; else v.entries.push(e); write(v); },
    async del(id) { const v = read(); v.entries = v.entries.filter((x) => x.id !== id); write(v); },
    async setRecent(list) { const v = read(); v.recent = list; write(v); },
  };
}

/* IndexedDB: "entries" (one record per op, keyPath id — the service worker
   reads and deletes them too) and "meta" (the recent list; the api base and
   a short-lived token for Background Sync). */
export function idbStore(idb: any, name = IDB_NAME): OutboxStore {
  let dbp: Promise<any> | null = null;
  const open = () => dbp || (dbp = new Promise((resolve, reject) => {
    const req = idb.open(name, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("entries")) db.createObjectStore("entries", { keyPath: "id" });
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "k" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => { dbp = null; reject(req.error || new Error("indexedDB open failed")); };
  }));
  const tx = async (store: string, mode: string, fn: (s: any) => any): Promise<any> => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const t = db.transaction(store, mode);
      const s = t.objectStore(store);
      let out: any;
      const r = fn(s);
      if (r && typeof r === "object" && "onsuccess" in r) r.onsuccess = () => { out = r.result; };
      t.oncomplete = () => resolve(out);
      t.onerror = () => reject(t.error || new Error("indexedDB write failed"));
      t.onabort = () => reject(t.error || new Error("indexedDB write aborted"));
    });
  };
  return {
    kind: "idb",
    async load() {
      const entries: OutboxEntry[] = (await tx("entries", "readonly", (s) => s.getAll())) || [];
      const rec = await tx("meta", "readonly", (s) => s.get("recent"));
      const acks = await tx("meta", "readonly", (s) => s.get("swAcks"));
      return { entries, recent: (rec && Array.isArray(rec.v)) ? rec.v : [], seq: entries.reduce((m, e) => Math.max(m, e.seq || 0), 0), acks: (acks && Array.isArray(acks.v)) ? acks.v : [] };
    },
    async clearAcks() { await tx("meta", "readwrite", (s) => s.delete("swAcks")); },
    async put(e) { await tx("entries", "readwrite", (s) => s.put(e)); },
    async del(id) { await tx("entries", "readwrite", (s) => s.delete(id)); },
    async setRecent(list) { await tx("meta", "readwrite", (s) => s.put({ k: "recent", v: list })); },
    async setMeta(k, v) { await tx("meta", "readwrite", (s) => s.put({ k, v })); },
  };
}

/* ============================================================
   The queue
   ============================================================ */
export type OutboxDeps = {
  store: OutboxStore;
  send: (e: OutboxEntry) => Promise<SendResult>;
  now?: () => number;
  isOnline?: () => boolean;
  setTimer?: (fn: () => void, ms: number) => any;
  clearTimer?: (t: any) => void;
  random?: () => number;
  newId?: () => string;
  /* one flush at a time across tabs (navigator.locks where there is one) */
  lock?: (fn: () => Promise<void>) => Promise<void>;
  onAck?: (e: OutboxEntry, outcome: "ok" | "held", body: any) => void;
  onFail?: (e: OutboxEntry) => void;
  onDrain?: (jobIds: string[]) => void;
  ws?: () => string | null;
};

export type OutboxView = { entries: OutboxEntry[]; recent: Recent[]; flushing: boolean; loaded: boolean; lastDrainAt: number; storage: string };

export function makeKey(): string {
  let rnd = "";
  try {
    const c: any = (globalThis as any).crypto;
    if (c && c.randomUUID) rnd = c.randomUUID().replace(/-/g, "");
    else { const b = new Uint8Array(16); c.getRandomValues(b); rnd = Array.from(b, (x: number) => x.toString(16).padStart(2, "0")).join(""); }
  } catch (e) { rnd = (Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2)).slice(0, 24); }
  return "op_" + Date.now().toString(36) + "_" + rnd.slice(0, 20);
}

export function createOutbox(deps: OutboxDeps) {
  const now = deps.now || (() => Date.now());
  const online = deps.isOnline || (() => true);
  const setTimer = deps.setTimer || ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer || ((t: any) => clearTimeout(t));
  const random = deps.random || Math.random;
  const lock = deps.lock || ((fn: () => Promise<void>) => fn());
  let entries: OutboxEntry[] = [];
  let recent: Recent[] = [];
  let seq = 0;
  let loaded = false, flushing: Promise<void> | null = null, again = false, stopped = false;
  let timer: any = null, lastDrainAt = 0;
  const unsaved = new Set<string>();   // queued in memory, still being written down
  const subs = new Set<(v: OutboxView) => void>();
  const waiters = new Map<string, ((r: any) => void)[]>();

  const sorted = () => entries.slice().sort((a, b) => (a.seq - b.seq) || (a.created_at - b.created_at));
  const view = (): OutboxView => ({ entries: sorted(), recent: recent.slice(), flushing: !!flushing, loaded, lastDrainAt, storage: deps.store.kind });
  const emit = () => { const v = view(); subs.forEach((fn) => { try { fn(v); } catch (e) { /* a listener never breaks the queue */ } }); };
  const settle = (id: string, r: any) => { const w = waiters.get(id); if (w) { waiters.delete(id); w.forEach((fn) => fn(r)); } };

  const ready = (async () => {
    try {
      const snap = await deps.store.load();
      // anything this tab queued before the load finished stays
      const have = new Set(entries.map((e) => e.id));
      entries = [...snap.entries.filter((e) => !have.has(e.id)), ...entries];
      recent = snap.recent || [];
      seq = Math.max(seq, snap.seq || 0, ...entries.map((e) => e.seq || 0));
    } catch (e) { /* storage unreadable: start empty, keep working in memory */ }
    loaded = true;
    pruneRecent();
    emit();
  })();

  function pruneRecent() {
    const cut = now() - RECENT_KEEP_MS;
    recent = recent.filter((r) => r && r.ackAt >= cut).slice(-RECENT_MAX);
  }
  async function remember(r: Recent) {
    recent = [...recent.filter((x) => x.id !== r.id), r];
    pruneRecent();
    try { await deps.store.setRecent(recent); } catch (e) { /* the recent list is a nicety */ }
  }

  /* Put an op on the queue — durable before this resolves. */
  async function enqueue(input: { id?: string; kind: FieldKind; jobId: string; payload?: any; created_at?: number }): Promise<OutboxEntry> {
    const e: OutboxEntry = {
      id: input.id || (deps.newId ? deps.newId() : makeKey()),
      kind: input.kind, jobId: String(input.jobId), payload: input.payload || {},
      created_at: Number(input.created_at) || now(),
      attempts: 0, last_error: null, status: "queued", seq: ++seq,
      ws: deps.ws ? deps.ws() : null,
      queuedOffline: !online(),
    };
    entries.push(e);
    unsaved.add(e.id);
    emit();
    try { await deps.store.put(e); } finally { unsaved.delete(e.id); }
    if (online()) kick(0);
    return e;
  }

  function kick(ms: number) {
    if (stopped) return;
    if (timer) clearTimer(timer);
    timer = setTimer(() => { timer = null; flush(); }, Math.max(0, ms));
  }

  /* Send what's due. Resolves once this pass (and any asked for while it
     ran) is done. */
  function flush(): Promise<void> {
    if (stopped) return Promise.resolve();
    if (flushing) { again = true; return flushing; }
    flushing = (async () => {
      await ready;
      emit();
      do { again = false; await lock(pass); } while (again && !stopped);
    })().finally(() => { flushing = null; emit(); schedule(); });
    return flushing;
  }

  async function pass() {
    if (!online()) return;
    // what's durable is the truth: the service worker may have sent (and
    // removed) some of these while the page was asleep
    try {
      const snap = await deps.store.load();
      const ids = new Set(snap.entries.map((e) => e.id));
      const mine = new Map(entries.map((e) => [e.id, e]));
      const acks = new Map((snap.acks || []).map((a) => [a.id, a]));
      entries = [...snap.entries.map((e) => ({ ...(mine.get(e.id) || {}), ...e })), ...entries.filter((e) => unsaved.has(e.id) && !ids.has(e.id))];
      for (const [id, e] of mine) {
        if (ids.has(id) || unsaved.has(id)) {
          const cur = entries.find((x) => x.id === id);
          if (cur && cur.status === "failed" && e.status !== "failed") { try { deps.onFail && deps.onFail(cur); } catch (x) { /* shown anyway */ } }
          continue;
        }
        // gone from storage: the service worker delivered it while the page slept
        const a: any = acks.get(id) || { outcome: "ok", body: { status: "applied" } };
        await remember({ id: e.id, kind: e.kind, jobId: e.jobId, created_at: e.created_at, payload: e.payload, ackAt: a.at || now(), result: a.outcome === "held" ? "held" : "applied", message: (a.body && a.body.message) || null, stamp: (a.body && a.body.stamp) || null });
        try { deps.onAck && deps.onAck(e, a.outcome === "held" ? "held" : "ok", a.body || {}); } catch (x) { /* the ack stands */ }
        settle(id, { outcome: a.outcome, body: a.body });
      }
      if (snap.acks && snap.acks.length && deps.store.clearAcks) await deps.store.clearAcks();
    } catch (e) { /* keep the in-memory copy */ }
    const byJob = new Map<string, OutboxEntry[]>();
    for (const e of sorted()) { const k = e.jobId || "_"; if (!byJob.has(k)) byJob.set(k, []); byJob.get(k)!.push(e); }
    const drained: string[] = [];
    let offline = false;
    for (const [jobId, list] of byJob) {
      if (offline) break;
      let cleared = true;
      for (const e of list) {
        if (e.status === "failed") { cleared = false; break; }           // waits behind a refused op: order holds
        if (e.nextAt && e.nextAt > now()) { cleared = false; break; }    // backing off
        let res: SendResult | null = null;
        try { res = await deps.send(e); } catch (err: any) { res = { status: 0, body: { message: String((err && err.message) || err) } }; }
        const out = classify(res);
        const body = (res && res.body) || {};
        if (out === "ok" || out === "held") {
          entries = entries.filter((x) => x.id !== e.id);
          await deps.store.del(e.id);
          await remember({ id: e.id, kind: e.kind, jobId: e.jobId, created_at: e.created_at, payload: e.payload, ackAt: now(), result: out === "held" ? "held" : "applied", message: body.message || null, stamp: body.stamp || null, dispatch: body.dispatch || null });
          try { deps.onAck && deps.onAck(e, out, body); } catch (x) { /* the ack stands */ }
          settle(e.id, { outcome: out, body });
          emit();
          continue;
        }
        cleared = false;
        const msg = String(body.message || body.error || (out === "offline" ? "No signal" : "Error " + (res && res.status))).slice(0, 300);
        const next: OutboxEntry = { ...e, attempts: e.attempts + 1, last_error: msg };
        if (out === "permanent") next.status = "failed";
        else next.nextAt = now() + backoffMs(next.attempts, random());
        entries = entries.map((x) => (x.id === e.id ? next : x));
        try { await deps.store.put(next); } catch (x) { /* retried from memory */ }
        if (out === "permanent") { try { deps.onFail && deps.onFail(next); } catch (x) { /* shown anyway */ } settle(e.id, { outcome: "permanent", body }); }
        if (out === "offline") offline = true;
        emit();
        break;
      }
      if (cleared && list.length) drained.push(jobId);
    }
    if (drained.length) {
      lastDrainAt = now();
      try { deps.onDrain && deps.onDrain(drained); } catch (x) { /* nothing depends on it */ }
    }
  }

  /* the next time something is due */
  function schedule() {
    if (stopped) return;
    // only the head of each job's queue can go next; a job whose head was
    // refused waits for the crew, not for a timer
    const heads = new Map<string, OutboxEntry>();
    for (const e of sorted()) if (!heads.has(e.jobId || "_")) heads.set(e.jobId || "_", e);
    const due = [...heads.values()].filter((e) => e.status === "queued").map((e) => e.nextAt || 0);
    if (!due.length) { if (timer) { clearTimer(timer); timer = null; } return; }
    kick(Math.max(250, Math.min(...due) - now()));
  }

  /* The crew's answer to a refused op. */
  async function discard(id: string): Promise<OutboxEntry | null> {
    await ready;
    const e = entries.find((x) => x.id === id);
    if (!e) return null;
    entries = entries.filter((x) => x.id !== id);
    await deps.store.del(id);
    await remember({ id: e.id, kind: e.kind, jobId: e.jobId, created_at: e.created_at, payload: e.payload, ackAt: now(), result: "discarded" });
    settle(id, { outcome: "discarded" });
    emit();
    flush();
    return e;
  }
  async function retry(id: string) {
    await ready;
    const e = entries.find((x) => x.id === id);
    if (!e) return;
    const next = { ...e, status: "queued" as const, nextAt: 0 };
    entries = entries.map((x) => (x.id === id ? next : x));
    await deps.store.put(next);
    emit();
    return flush();
  }
  /* resolves with the service's answer once this op is delivered (or
     refused), or { outcome: "waiting" } after ms */
  function settled(id: string, ms: number): Promise<any> {
    return new Promise((resolve) => {
      let done = false;
      const t = setTimer(() => { if (!done) { done = true; resolve({ outcome: "waiting" }); } }, ms);
      const list = waiters.get(id) || [];
      list.push((r) => { if (!done) { done = true; clearTimer(t); resolve(r); } });
      waiters.set(id, list);
    });
  }

  return {
    ready, enqueue, flush, discard, retry, settled,
    view,
    list: () => sorted(),
    recent: () => recent.slice(),
    /* jobs with an op still on its way (not a refused one): the diff sync
       holds those rows back so the service sees the ops first */
    pendingJobs: () => new Set(entries.filter((e) => e.status === "queued").map((e) => e.jobId)),
    subscribe(fn: (v: OutboxView) => void) { subs.add(fn); return () => { subs.delete(fn); }; },
    stop() { stopped = true; if (timer) clearTimer(timer); timer = null; subs.clear(); },
  };
}
export type Outbox = ReturnType<typeof createOutbox>;

/* ============================================================
   In the app (fieldsync.ts wires it to the store)
   ============================================================ */
/* the best durable store this browser has */
export function defaultStore(): OutboxStore {
  const g: any = globalThis as any;
  try { if (g.indexedDB && typeof g.indexedDB.open === "function") return idbStore(g.indexedDB); } catch (e) { /* fall through */ }
  try { if (g.localStorage) { g.localStorage.getItem(LS_KEY); return localStore(g.localStorage); } } catch (e) { /* fall through */ }
  return memoryStore();
}

/* IndexedDB can be there and still refuse (private windows, a full disk):
   try it once, fall back to localStorage */
export async function pickStore(): Promise<OutboxStore> {
  const first = defaultStore();
  if (first.kind !== "idb") return first;
  try { await first.load(); return first; } catch (e) {
    const g: any = globalThis as any;
    try { if (g.localStorage) return localStore(g.localStorage); } catch (x) { /* fall through */ }
    return memoryStore();
  }
}

/* Plain words for the crew. */
export function opLabel(e: { kind: string; payload?: any }): string {
  const p = e.payload || {};
  switch (e.kind) {
    case "clock.in": return p.override ? "Clock in (office override)" : "Clock in";
    case "clock.out": return "Clock out" + (p.entry && p.entry.hours ? " · " + p.entry.hours + " hr" : "");
    case "break.start": return "Break";
    case "break.end": return "Back from break";
    case "job.enroute": return "On my way";
    case "job.start": return "Started the job";
    case "job.complete": return "Job done";
    case "job.note": return "Note: " + String((p.note && p.note.text) || "").slice(0, 60);
    case "job.liftlog": {
      if (p.removeId) return "Lift log entry removed";
      const e = p.entry || {};
      return "Lift log: " + String(e.section || "section").slice(0, 40) + (e.foamLb ? " · " + e.foamLb + " lb" : "") + (e.beforeIn != null && e.afterIn != null ? " · " + e.beforeIn + "″ → " + e.afterIn + "″" : "");
    }
    case "job.check": {
      const label = checkLabel(p.item);
      return (p.done ? "✓ " : "Unticked ") + (label || String(p.item || "checklist"));
    }
    default: return "Update";
  }
}

export const hhmm = (t: any) => (Number(t) > 0 ? new Date(Number(t)).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");
/* "entered offline at 7:02, synced 9:40" (with the day when it isn't today) */
export function offlineText(stamp: any, nowMs = Date.now()) {
  if (!stamp || !stamp.at) return "";
  const day = (t: number) => { const d = new Date(t), n = new Date(nowMs); return d.toDateString() === n.toDateString() ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " "; };
  let s = "entered offline at " + day(stamp.at) + hhmm(stamp.at);
  if (stamp.syncedAt) s += ", synced " + day(stamp.syncedAt) + hhmm(stamp.syncedAt);
  if (stamp.approvedAt) s += " · approved by the office";
  return s;
}
